import { createTheme } from "@mui/material/styles";

// Default theme colors
const DEFAULT_THEME = {
  primary: "#000000",
  secondary: "#0066cc",
  icon: "#000000",
  button: "#ffd700",
  text: "#000000",
  background: "#ffffff",
  surface: "#f5f5f5",
  error: "#dc3545",
  warning: "#ffc107",
  info: "#17a2b8",
  success: "#28a745",
};

/**
 * Parse theme from string or object
 * @param {string|object} themeData - Theme data from API
 * @returns {object} Parsed theme object
 */
export const parseTheme = (themeData) => {
  if (!themeData) return DEFAULT_THEME;

  try {
    const parsed =
      typeof themeData === "string" ? JSON.parse(themeData) : themeData;
    return {
      ...DEFAULT_THEME,
      ...parsed,
    };
  } catch (error) {
    console.warn("Failed to parse theme data:", error);
    return DEFAULT_THEME;
  }
};

/**
 * Create Material-UI theme with custom colors
 * @param {object} customTheme - Custom theme colors
 * @returns {object} Material-UI theme
 */
export const createCustomTheme = (customTheme = DEFAULT_THEME) => {
  const theme = parseTheme(customTheme);

  return createTheme({
    palette: {
      mode: "light",
      primary: {
        main: theme.primary,
        contrastText: getContrastColor(theme.primary),
      },
      secondary: {
        main: theme.secondary,
        contrastText: getContrastColor(theme.secondary),
      },
      error: {
        main: theme.error,
      },
      warning: {
        main: theme.warning,
      },
      info: {
        main: theme.info,
      },
      success: {
        main: theme.success,
      },
      background: {
        default: theme.background,
        paper: theme.surface,
      },
      text: {
        primary: theme.text,
        secondary: adjustOpacity(theme.text, 0.7),
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: "2.5rem",
        lineHeight: 1.2,
        color: theme.text,
      },
      h2: {
        fontWeight: 700,
        fontSize: "2rem",
        lineHeight: 1.2,
        color: theme.text,
      },
      h3: {
        fontWeight: 600,
        fontSize: "1.75rem",
        lineHeight: 1.3,
        color: theme.text,
      },
      h4: {
        fontWeight: 600,
        fontSize: "1.5rem",
        lineHeight: 1.4,
        color: theme.text,
      },
      h5: {
        fontWeight: 600,
        fontSize: "1.25rem",
        lineHeight: 1.4,
        color: theme.text,
      },
      h6: {
        fontWeight: 600,
        fontSize: "1rem",
        lineHeight: 1.5,
        color: theme.text,
      },
      button: {
        textTransform: "none",
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            backgroundColor: theme.button,
            color: getContrastColor(theme.button),
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: adjustBrightness(theme.button, -20),
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.surface,
              borderRadius: "12px",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: adjustBrightness(theme.surface, 5),
              },
              "&.Mui-focused": {
                backgroundColor: theme.background,
                "& fieldset": {
                  borderColor: theme.primary,
                  borderWidth: "2px",
                },
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.text,
              "&.Mui-focused": {
                color: theme.primary,
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: theme.background,
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            marginBottom: "16px",
          },
        },
      },
    },
  });
};

/**
 * Get contrast color (black or white) based on background
 * @param {string} hexColor - Hex color code
 * @returns {string} Contrast color
 */
const getContrastColor = (hexColor) => {
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? "#000000" : "#ffffff";
};

/**
 * Adjust color brightness
 * @param {string} color - Hex color code
 * @param {number} amount - Amount to adjust (-100 to 100)
 * @returns {string} Adjusted color
 */
const adjustBrightness = (color, amount) => {
  const usePound = color[0] === "#";
  const col = usePound ? color.slice(1) : color;
  const num = parseInt(col, 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00ff) + amount;
  let b = (num & 0x0000ff) + amount;

  r = r > 255 ? 255 : r < 0 ? 0 : r;
  g = g > 255 ? 255 : g < 0 ? 0 : g;
  b = b > 255 ? 255 : b < 0 ? 0 : b;

  return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
};

/**
 * Adjust color opacity
 * @param {string} color - Hex color code
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} RGBA color
 */
const adjustOpacity = (color, opacity) => {
  const hex = color.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export { DEFAULT_THEME };
